import {useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {
    Bar,
    CartesianGrid,
    Cell,
    ComposedChart,
    Line,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    ArrowLeft,
    ArrowRight,
    BarChart3,
    CreditCard,
    Download,
    Filter,
    HandCoins,
    Landmark,
    LayoutGrid,
    List,
    ReceiptText,
    RotateCcw,
    Search,
    Split,
} from "lucide-react";
import {Link} from "react-router-dom";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {AdminTable} from "../../shared/components/AdminTable";
import {ChartTooltip} from "../../shared/components/ChartTooltip";
import {DataState} from "../../shared/components/DataState";
import {PageSizeSelect} from "../../shared/components/PageSizeSelect";
import {PageToolbar, ToolbarButton, ToolbarInput, ToolbarSelect} from "../../shared/components/PageToolbar";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {useTranslation} from "../../shared/i18n";
import {asList, formatDateTime, formatMoney, formatNumber, getCount} from "../../shared/utils/format";
import {
    DEFAULT_PAY_REPORT_PAGE_SIZE,
    buildPayReportDownloadUrl,
    exportPayReportSpreadsheet,
    loadClubReport,
    loadPayPlaceGroups,
    loadPayReport,
    loadTopupReport,
    searchPayNames,
} from "./api";
import {GroupedPayTable} from "./GroupedPayTable";
import "./FinancePage.css";

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const PAY_VIEW_STORAGE_KEY = "sorpa-admin-pay-view";
const PAY_VIEWS = {
    groups: {
        queryKey: "admin-pay-place-groups",
        queryFn: loadPayPlaceGroups,
    },
    transactions: {
        queryKey: "admin-pay-report",
        queryFn: loadPayReport,
    },
};

const readStoredPayView = () => {
    try {
        return localStorage.getItem(PAY_VIEW_STORAGE_KEY) === "groups" ? "groups" : "transactions";
    } catch (_error) {
        return "transactions";
    }
};

const emptyDraft = {
    date_from: "",
    date_to: "",
    place: "",
    place_label: "",
    branch: "",
    branch_label: "",
    role: "",
};

const statusTone = (status) => {
    const value = String(status || "").toLowerCase();
    if (["success", "paid", "completed", "успешно", "принят", "оплачен"].some((item) => value.includes(item))) {
        return "success";
    }
    if (["cancel", "fail", "error", "отмен", "ошиб"].some((item) => value.includes(item))) {
        return "danger";
    }
    if (["pending", "process", "ожид", "draft"].some((item) => value.includes(item))) {
        return "warning";
    }
    return "neutral";
};

const amountToneClass = (value) => (Number(value || 0) < 0 ? "is-danger" : "is-money");

const chartColors = ["#57d7a7", "#b9f06b", "#65d6ff", "#f8c663", "#b8a7ff", "#f97066"];

const toNumber = (value) => {
    const number = Number(value || 0);
    return Number.isFinite(number) ? number : 0;
};

const compactMoney = (value) =>
    new Intl.NumberFormat("ru-RU", {
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(toNumber(value));

const dayLabel = (value) => {
    if (!value) {
        return "";
    }
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
        return new Intl.DateTimeFormat("ru-RU", {day: "2-digit", month: "2-digit"}).format(date);
    }
    return String(value).split(/[T, ]/)[0] || "";
};

const rowAmount = (type, row) => {
    if (type === "topups") {
        return toNumber(row.amount);
    }
    if (type === "club") {
        return toNumber(row.pay_sum);
    }
    return toNumber(row.sum);
};

const sumRows = (rows, key) => rows.reduce((total, row) => total + toNumber(row[key]), 0);

const buildTrendData = (type, rows, report) => {
    if (type === "pay" && Array.isArray(report?.trend) && report.trend.length) {
        return report.trend.slice(-12).map((point) => ({
            name: dayLabel(point.date) || point.date,
            gross: toNumber(point.sum),
            net: toNumber(point.partner_sum),
            commission: toNumber(point.commission),
            count: toNumber(point.count),
        }));
    }
    const grouped = new Map();
    rows.forEach((row) => {
        const key = dayLabel(row.created_at || row.date || row.updated_at);
        if (!key) {
            return;
        }
        const current = grouped.get(key) || {name: key, gross: 0, net: 0, commission: 0, count: 0};
        current.gross += rowAmount(type, row);
        current.net += type === "pay" ? toNumber(row.partner_sum) : rowAmount(type, row);
        current.commission += type === "pay" ? toNumber(row.commission) : 0;
        current.count += 1;
        grouped.set(key, current);
    });
    return Array.from(grouped.values()).slice(-12);
};

const buildBreakdownData = (type, report, rows, t) => {
    const source = rows.length ? rows : [];
    const data = type === "pay"
        ? [
            {name: t("money.finance.breakdown.partner"), value: sumRows(source, "partner_sum")},
            {name: t("money.finance.breakdown.commission"), value: sumRows(source, "commission")},
            {name: "Plus", value: sumRows(source, "bonus_pay")},
            {name: "Extra", value: sumRows(source, "balance_add_pay")},
            {name: t("money.finance.breakdown.returns"), value: sumRows(source, "return_sum")},
        ]
        : type === "topups"
            ? Object.values(
                source.reduce((acc, row) => {
                    const key = row.role || t("money.finance.breakdown.noRole");
                    acc[key] = acc[key] || {name: key, value: 0};
                    acc[key].value += toNumber(row.amount);
                    return acc;
                }, {}),
            )
            : [
                {name: t("money.finance.breakdown.payable"), value: toNumber(report?.bills_sum)},
                {name: "Gross", value: toNumber(report?.bills_sum_b)},
                {name: t("money.finance.breakdown.partners"), value: toNumber(report?.branch_sum)},
                {name: "Plus", value: toNumber(report?.bonus_sum)},
                {name: "Extra", value: toNumber(report?.bonus_add_sum)},
            ];

    return data.filter((item) => Math.abs(item.value) > 0).slice(0, 6);
};

const buildStatusData = (type, rows, report) => {
    if (type === "pay" && Array.isArray(report?.statuses) && report.statuses.length) {
        return report.statuses
            .map((item) => ({
                name: item.name,
                count: toNumber(item.count),
                amount: toNumber(item.amount),
            }))
            .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
            .slice(0, 5);
    }
    const field = type === "topups" ? "bill_status" : type === "pay" ? "status" : "role";
    return Object.values(
        rows.reduce((acc, row) => {
            const key = row[field];
            if (!key) {
                return acc;
            }
            acc[key] = acc[key] || {name: key, count: 0, amount: 0};
            acc[key].count += 1;
            acc[key].amount += rowAmount(type, row);
            return acc;
        }, {}),
    ).sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount)).slice(0, 5);
};

const buildTopEntities = (type, rows, t) => {
    const getName = (row) => {
        if (type === "pay") {
            return row.branch_name || row.place_name || row.username || t("money.finance.top.noBranch");
        }
        if (type === "topups") {
            return row.name || row.phone_number || t("money.finance.top.noName");
        }
        return row.name || row.phone_number || t("money.finance.top.participant");
    };

    return Object.values(
        rows.reduce((acc, row) => {
            const key = getName(row);
            acc[key] = acc[key] || {name: key, amount: 0, count: 0};
            acc[key].amount += rowAmount(type, row);
            acc[key].count += 1;
            return acc;
        }, {}),
    ).sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount)).slice(0, 6);
};

const pageFromLink = (link) => {
    if (!link) {
        return null;
    }
    try {
        const url = new URL(link);
        return Number(url.searchParams.get("page") || 1);
    } catch {
        const match = String(link).match(/[?&]page=(\d+)/);
        return match ? Number(match[1]) : null;
    }
};

const cleanFiltersForType = (type, draft, page, pageSize = DEFAULT_PAY_REPORT_PAGE_SIZE) => {
    const base = {
        date_from: draft.date_from,
        date_to: draft.date_to,
        page,
        ...(Number(pageSize) === DEFAULT_PAY_REPORT_PAGE_SIZE ? {} : {page_size: pageSize}),
    };
    const raw = type === "pay"
        ? {...base, place: draft.place, branch: draft.branch}
        : type === "topups"
            ? {...base, role: draft.role}
            : base;
    return Object.fromEntries(
        Object.entries(raw).filter(([, value]) => value !== "" && value !== null && value !== undefined),
    );
};

const activeFilterLabels = (type, draft, t) => {
    const labels = [];
    if (draft.date_from || draft.date_to) {
        labels.push(t("money.finance.filters.range", {
            from: draft.date_from || t("money.finance.filters.rangeStart"),
            to: draft.date_to || t("money.finance.filters.rangeEnd"),
        }));
    }
    if (type === "pay" && draft.place) {
        labels.push(t("money.finance.filters.placeTag", {name: draft.place_label || draft.place}));
    }
    if (type === "pay" && draft.branch) {
        labels.push(t("money.finance.filters.branchTag", {name: draft.branch_label || draft.branch}));
    }
    if (type === "topups" && draft.role) {
        labels.push(draft.role === "1" ? t("money.finance.filters.users") : t("money.finance.filters.clubMembers"));
    }
    return labels;
};

const datasetColumns = (type, t) => {
    if (type === "topups") {
        return [
            {key: "created_at", label: t("money.finance.columns.date"), render: (row) => formatDateTime(row.created_at)},
            {key: "bill_id", label: t("money.finance.columns.billId"), render: (row) => <span className="admin-mono">{row.bill_id || row.id}</span>},
            {key: "name", label: t("money.finance.columns.user"), render: (row) => row.name || "—"},
            {key: "phone_number", label: t("money.finance.columns.phone"), render: (row) => row.phone_number || "—"},
            {key: "role", label: t("money.finance.columns.role"), render: (row) => <StatusBadge tone="neutral">{row.role}</StatusBadge>},
            {key: "bill_status", label: t("money.finance.columns.status"), render: (row) => <StatusBadge tone={statusTone(row.bill_status)}>{row.bill_status}</StatusBadge>},
            {key: "amount", label: t("money.finance.columns.amount"), render: (row) => <span className="admin-strong">{formatMoney(row.amount)}</span>},
        ];
    }
    if (type === "club") {
        return [
            {key: "id", label: "ID", render: (row) => row.id},
            {key: "name", label: t("money.finance.columns.participant"), render: (row) => row.name || "—"},
            {key: "phone_number", label: t("money.finance.columns.phone"), render: (row) => row.phone_number || "—"},
            {key: "club_part", label: t("money.finance.columns.share"), render: (row) => `${formatNumber(row.club_part)} / 1000`},
            {key: "pay_sum", label: t("money.finance.secondary.payable"), render: (row) => <span className={`admin-strong ${amountToneClass(row.pay_sum)}`}>{formatMoney(row.pay_sum)}</span>},
        ];
    }
    return [
        {key: "created_at", label: t("money.finance.columns.date"), render: (row) => formatDateTime(row.created_at)},
        {key: "id", label: t("money.finance.columns.operation"), render: (row) => <span className="admin-mono">{row.id || "—"}</span>},
        {key: "username", label: t("money.finance.columns.payer"), render: (row) => row.username || "—"},
        {key: "place_name", label: t("money.finance.columns.place"), render: (row) => row.place_name || row.branch_name || "—"},
        {key: "branch_name", label: t("money.finance.columns.branch"), render: (row) => row.branch_name || "—"},
        {key: "status", label: t("money.finance.columns.status"), render: (row) => <StatusBadge tone={statusTone(row.status)}>{row.status}</StatusBadge>},
        {key: "sum", label: t("money.finance.columns.amount"), render: (row) => <span className="admin-strong">{formatMoney(row.sum)}</span>},
        {key: "commission", label: t("money.finance.columns.commission"), render: (row) => formatMoney(row.commission)},
        {key: "partner_sum", label: t("money.finance.columns.partner"), render: (row) => formatMoney(row.partner_sum)},
        {key: "bonus_pay", label: "Plus", render: (row) => formatMoney(row.bonus_pay)},
        {key: "balance_add_pay", label: "Extra", render: (row) => formatMoney(row.balance_add_pay)},
        {key: "return_sum", label: t("money.finance.columns.refund"), render: (row) => formatMoney(row.return_sum)},
    ];
};

const datasetMeta = (type, t) => {
    if (type === "topups") {
        return {
            title: t("money.finance.datasets.topups.title"),
            copy: t("money.finance.datasets.topups.copy"),
            queryKey: "admin-topups-report",
            queryFn: loadTopupReport,
            emptyText: t("money.finance.datasets.topups.empty"),
            totalLabel: t("money.finance.datasets.topups.total"),
            totalKey: "bills_sum",
            secondary: [],
        };
    }
    if (type === "club") {
        return {
            title: t("money.finance.datasets.club.title"),
            copy: t("money.finance.datasets.club.copy"),
            queryKey: "admin-club-report",
            queryFn: loadClubReport,
            emptyText: t("money.finance.datasets.club.empty"),
            totalLabel: t("money.finance.datasets.club.total"),
            totalKey: "bills_sum",
            secondary: [
                {label: "Gross", key: "bills_sum_b"},
                {label: t("money.finance.secondary.partners"), key: "branch_sum"},
                {label: "Plus", key: "bonus_sum"},
                {label: "Extra", key: "bonus_add_sum"},
            ],
        };
    }
    return {
        title: t("money.finance.datasets.pay.title"),
        copy: t("money.finance.payViews.groupsCopy"),
        queryKey: PAY_VIEWS.groups.queryKey,
        queryFn: PAY_VIEWS.groups.queryFn,
        emptyText: t("money.finance.datasets.pay.empty"),
        totalLabel: "Gross",
        totalKey: "sum",
        secondary: [
            {label: t("money.finance.secondary.payable"), key: "pay_sum"},
            {label: t("money.finance.secondary.commission"), key: "partner_sum"},
        ],
    };
};

const FinancialCharts = ({type, report, rows, loading}) => {
    const {t} = useTranslation();
    const trendData = useMemo(() => buildTrendData(type, rows, report), [type, rows, report]);
    const breakdownData = useMemo(() => buildBreakdownData(type, report, rows, t), [type, report, rows, t]);
    const statusData = useMemo(() => buildStatusData(type, rows, report), [type, rows, report]);
    const topEntities = useMemo(() => buildTopEntities(type, rows, t), [type, rows, t]);
    const totalVisible = type === "pay"
        ? toNumber(report?.sum)
        : rows.reduce((total, row) => total + rowAmount(type, row), 0);
    const maxStatusAmount = Math.max(...statusData.map((item) => Math.abs(item.amount)), 1);
    const maxTopAmount = Math.max(...topEntities.map((item) => Math.abs(item.amount)), 1);

    return (
        <section className="admin-finance-analytics">
            <article className="admin-card admin-chart-card admin-finance-trend-card">
                <div className="admin-chart-header">
                    <div>
                        <p className="admin-card-title"><BarChart3 size={14} /> {t("money.finance.charts.trendTitle")}</p>
                        <p className="admin-card-note">{t(`money.finance.charts.${type}.trend`)}</p>
                    </div>
                    <strong>{compactMoney(totalVisible)}</strong>
                </div>
                <div className="admin-chart-area">
                    {loading ? (
                        <div className="admin-chart-empty">{t("money.finance.charts.loading")}</div>
                    ) : trendData.length ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={trendData} margin={{top: 8, right: 6, bottom: 0, left: 0}}>
                                <CartesianGrid stroke="rgba(214,229,222,0.06)" vertical={false} />
                                <XAxis dataKey="name" stroke="#8ea0ad" fontSize={11} tickLine={false} axisLine={false} />
                                <YAxis
                                    stroke="#8ea0ad"
                                    fontSize={11}
                                    tickLine={false}
                                    axisLine={false}
                                    tickFormatter={compactMoney}
                                    width={54}
                                />
                                <Tooltip
                                    cursor={{fill: "rgba(87, 215, 167, 0.045)"}}
                                    content={<ChartTooltip formatter={formatMoney} />}
                                />
                                <Bar dataKey="gross" name={type === "topups" ? t("money.finance.charts.toppedUp") : "Gross"} fill="#57d7a7" radius={[5, 5, 0, 0]} />
                                {type === "pay" && (
                                    <>
                                        <Line type="monotone" dataKey="net" name={t("money.finance.charts.partner")} stroke="#65d6ff" strokeWidth={2.4} dot={false} />
                                        <Line type="monotone" dataKey="commission" name={t("money.finance.charts.commission")} stroke="#f8c663" strokeWidth={2.4} dot={false} />
                                    </>
                                )}
                            </ComposedChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="admin-chart-empty">{t("money.finance.charts.empty")}</div>
                    )}
                </div>
            </article>

            <article className="admin-card admin-chart-card admin-finance-breakdown-card">
                <div className="admin-chart-header">
                    <div>
                        <p className="admin-card-title">{t("money.finance.charts.composition")}</p>
                        <p className="admin-card-note">{t(`money.finance.charts.${type}.breakdown`)}</p>
                    </div>
                </div>
                <div className="admin-donut-layout">
                    <div className="admin-donut-chart">
                        {breakdownData.length ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={breakdownData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius="58%"
                                        outerRadius="88%"
                                        paddingAngle={2}
                                        stroke="rgba(8,11,12,0.72)"
                                        strokeWidth={2}
                                    >
                                        {breakdownData.map((entry, index) => (
                                            <Cell key={entry.name} fill={chartColors[index % chartColors.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        content={<ChartTooltip formatter={formatMoney} valueLabel={t("money.finance.charts.amount")} />}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="admin-chart-empty">{t("money.finance.charts.noComposition")}</div>
                        )}
                    </div>
                    <div className="admin-chart-legend">
                        {breakdownData.map((item, index) => (
                            <div key={item.name}>
                                <span style={{background: chartColors[index % chartColors.length]}} />
                                <strong>{item.name}</strong>
                                <em>{formatMoney(item.value)}</em>
                            </div>
                        ))}
                    </div>
                </div>
            </article>

            <article className="admin-card admin-finance-insight-card">
                <div className="admin-chart-header">
                    <div>
                        <p className="admin-card-title">{t("money.finance.charts.statuses")}</p>
                        <p className="admin-card-note">{t("money.finance.charts.statusesNote")}</p>
                    </div>
                </div>
                <div className="admin-status-bars">
                    {statusData.length ? statusData.map((item, index) => (
                        <div key={item.name} className="admin-status-bar-row">
                            <div>
                                <strong>{item.name}</strong>
                                <span>{t("money.finance.charts.operations", {count: formatNumber(item.count)})}</span>
                            </div>
                            <em>{formatMoney(item.amount)}</em>
                            <span style={{width: `${Math.max(8, (Math.abs(item.amount) / maxStatusAmount) * 100)}%`, background: chartColors[index % chartColors.length]}} />
                        </div>
                    )) : <div className="admin-chart-empty">{t("money.finance.charts.statusesEmpty")}</div>}
                </div>
            </article>

            <article className="admin-card admin-finance-insight-card">
                <div className="admin-chart-header">
                    <div>
                        <p className="admin-card-title">{t("money.finance.charts.top")}</p>
                        <p className="admin-card-note">{t(`money.finance.charts.${type}.top`)}</p>
                    </div>
                </div>
                <div className="admin-insight-list">
                    {topEntities.length ? topEntities.map((item, index) => (
                        <div key={item.name} className="admin-insight-row">
                            <span>{index + 1}</span>
                            <div>
                                <strong>{item.name}</strong>
                                <em>{t("money.finance.charts.operations", {count: formatNumber(item.count)})}</em>
                                <i style={{width: `${Math.max(10, (Math.abs(item.amount) / maxTopAmount) * 100)}%`}} />
                            </div>
                            <b>{formatMoney(item.amount)}</b>
                        </div>
                    )) : <div className="admin-chart-empty">{t("money.finance.charts.topEmpty")}</div>}
                </div>
            </article>
        </section>
    );
};

const FinanceDataset = ({type, filters, pageSize, payView = "groups", onPayViewChange, onPageChange, onPageSizeChange}) => {
    const {t} = useTranslation();
    const payMode = type === "pay" ? PAY_VIEWS[payView] || PAY_VIEWS.transactions : null;
    const config = {
        ...datasetMeta(type, t),
        columns: datasetColumns(type, t),
        ...(payMode ? {
            queryKey: payMode.queryKey,
            queryFn: payMode.queryFn,
            copy: t(payView === "groups" ? "money.finance.payViews.groupsCopy" : "money.finance.payViews.transactionsCopy"),
        } : {}),
    };
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState("");
    const reportQuery = useQuery({
        queryKey: [config.queryKey, filters],
        queryFn: () => config.queryFn(filters),
    });
    const rows = asList(reportQuery.data);
    const nextPage = pageFromLink(reportQuery.data?.next);
    const previousPage = pageFromLink(reportQuery.data?.previous);
    const currentPage = Number(filters.page || 1);
    const canExport = type === "pay";

    const handleExport = async () => {
        if (!canExport || exporting) return;
        setExporting(true);
        setExportError("");
        try {
            await exportPayReportSpreadsheet(
                reportQuery.data?.download || buildPayReportDownloadUrl(filters),
                config.title || "pay-report",
            );
        } catch (_error) {
            setExportError(t("money.finance.export.failed"));
        } finally {
            setExporting(false);
        }
    };

    return (
        <>
            <section className="admin-grid cols-4">
                <article className="admin-card">
                    <p className="admin-card-title">{t("money.finance.metrics.operations")}</p>
                    <p className="admin-card-value">{formatNumber(getCount(reportQuery.data))}</p>
                    <p className="admin-card-note">{t("money.finance.metrics.operationsNote")}</p>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title">{config.totalLabel}</p>
                    <p className={`admin-card-value ${amountToneClass(reportQuery.data?.[config.totalKey])}`}>
                        {formatMoney(reportQuery.data?.[config.totalKey])}
                    </p>
                    <p className="admin-card-note">{t("money.finance.metrics.aggregateNote")}</p>
                </article>
                {config.secondary.slice(0, 2).map((item) => (
                    <article className="admin-card" key={item.key}>
                        <p className="admin-card-title">{item.label}</p>
                        <p className={`admin-card-value ${amountToneClass(reportQuery.data?.[item.key])}`}>
                            {formatMoney(reportQuery.data?.[item.key])}
                        </p>
                        <p className="admin-card-note">{t("money.finance.metrics.filterNote")}</p>
                    </article>
                ))}
                {config.secondary.length < 2 && (
                    <article className="admin-card">
                        <p className="admin-card-title">{t("money.finance.metrics.page")}</p>
                        <p className="admin-card-value">{formatNumber(currentPage)}</p>
                        <p className="admin-card-note">{t("money.finance.metrics.rows", {count: formatNumber(rows.length)})}</p>
                    </article>
                )}
            </section>

            <FinancialCharts type={type} report={reportQuery.data} rows={rows} loading={reportQuery.isLoading} />

            {config.secondary.length > 2 && (
                <section className="admin-kpi-row">
                    {config.secondary.slice(2).map((item) => (
                        <span className={`admin-mini-kpi ${amountToneClass(reportQuery.data?.[item.key])}`} key={item.key}>
                            {item.label}: {formatMoney(reportQuery.data?.[item.key])}
                        </span>
                    ))}
                </section>
            )}

            <section className="admin-card">
                <div className="admin-section-head">
                    <div>
                        <p className="admin-card-title">{config.title}</p>
                        <p className="admin-card-note">{config.copy}</p>
                        {exportError ? <p className="admin-card-note" role="alert">{exportError}</p> : null}
                    </div>
                    <div className="finance-table-actions">
                        {type === "pay" ? (
                            <div className="admin-tabs finance-view-tabs" role="tablist" aria-label={t("money.finance.payViews.aria")}>
                                <button
                                    className={`admin-tab ${payView === "groups" ? "is-active" : ""}`}
                                    type="button"
                                    role="tab"
                                    aria-selected={payView === "groups"}
                                    onClick={() => onPayViewChange("groups")}
                                >
                                    <LayoutGrid size={14} /> {t("money.finance.payViews.groups")}
                                </button>
                                <button
                                    className={`admin-tab ${payView === "transactions" ? "is-active" : ""}`}
                                    type="button"
                                    role="tab"
                                    aria-selected={payView === "transactions"}
                                    onClick={() => onPayViewChange("transactions")}
                                >
                                    <List size={14} /> {t("money.finance.payViews.transactions")}
                                </button>
                            </div>
                        ) : null}
                        <PageSizeSelect
                            value={pageSize}
                            options={PAGE_SIZE_OPTIONS}
                            disabled={reportQuery.isLoading}
                            onChange={onPageSizeChange}
                        />
                        {canExport ? (
                            <button
                                type="button"
                                className="admin-button admin-button-secondary"
                                disabled={exporting || reportQuery.isLoading}
                                onClick={handleExport}
                            >
                                <Download size={15} /> {exporting ? t("money.finance.export.pending") : t("money.finance.export.label")}
                            </button>
                        ) : null}
                    </div>
                </div>
                {reportQuery.isError ? (
                    <DataState
                        title={t("money.finance.error.title")}
                        copy={t("money.finance.error.copy")}
                    />
                ) : (
                    <>
                        {type === "pay" && payView === "groups" ? (
                            <GroupedPayTable
                                groups={rows}
                                detailColumns={config.columns}
                                filters={filters}
                                loading={reportQuery.isLoading}
                                emptyText={config.emptyText}
                            />
                        ) : (
                            <AdminTable columns={config.columns} rows={rows} loading={reportQuery.isLoading} emptyText={config.emptyText} />
                        )}
                        <ReportPagination
                            currentPage={currentPage}
                            loading={reportQuery.isLoading}
                            nextPage={nextPage}
                            onPageChange={onPageChange}
                            previousPage={previousPage}
                        />
                    </>
                )}
            </section>
        </>
    );
};

const ReportPagination = ({currentPage, previousPage, nextPage, loading, onPageChange}) => {
    const {t} = useTranslation();
    return (
    <div className="admin-report-pagination">
        <ToolbarButton disabled={!previousPage || loading} variant="secondary" onClick={() => onPageChange(previousPage)}>
            <ArrowLeft size={15} /> {t("money.finance.back")}
        </ToolbarButton>
        <span>{t("money.finance.page", {page: formatNumber(currentPage)})}</span>
        <ToolbarButton disabled={!nextPage || loading} variant="secondary" onClick={() => onPageChange(nextPage)}>
            {t("money.finance.forward")} <ArrowRight size={15} />
        </ToolbarButton>
    </div>
    );
};

const PayNameFilter = ({kind, value, label, placeholder, onSelect, onClear}) => {
    const {t} = useTranslation();
    const [term, setTerm] = useState(label || "");
    const searchQuery = useQuery({
        queryKey: ["admin-pay-search", kind, term],
        queryFn: () => searchPayNames(kind === "place" ? {place_name: term} : {branch_name: term}),
        enabled: term.trim().length >= 2 && term !== label,
    });
    const results = asList(searchQuery.data).slice(0, 6);

    const handleSelect = (item) => {
        setTerm(item.name);
        onSelect(item);
    };

    const handleClear = () => {
        setTerm("");
        onClear();
    };

    return (
        <div className="admin-smart-filter">
            <ToolbarInput
                label={label
                    ? t(kind === "place" ? "money.finance.filters.placeId" : "money.finance.filters.branchId", {id: value})
                    : t(kind === "place" ? "money.finance.columns.place" : "money.finance.columns.branch")}
                placeholder={placeholder}
                value={term}
                onChange={(event) => {
                    setTerm(event.target.value);
                    if (/^\d+$/.test(event.target.value.trim())) {
                        onSelect({id: event.target.value.trim(), name: `#${event.target.value.trim()}`});
                    }
                }}
            />
            {(value || term) && (
                <button className="admin-filter-clear" type="button" onClick={handleClear}>
                    {t("money.finance.reset")}
                </button>
            )}
            {results.length > 0 && (
                <div className="admin-filter-suggestions">
                    {results.map((item) => (
                        <button key={item.id} type="button" onClick={() => handleSelect(item)}>
                            <strong>{item.name}</strong>
                            <span>#{item.id}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export const FinancePage = () => {
    const {t} = useTranslation();
    const capabilities = useCapabilities();
    const [draft, setDraft] = useState(emptyDraft);
    const [filters, setFilters] = useState({page: 1});
    const [pageSize, setPageSize] = useState(DEFAULT_PAY_REPORT_PAGE_SIZE);
    const [type, setType] = useState("pay");
    const [payView, setPayView] = useState(readStoredPayView);

    const tabs = useMemo(
        () => [
            {key: "pay", label: t("money.finance.tabs.pay"), icon: ReceiptText},
            {key: "topups", label: t("money.finance.tabs.topups"), icon: CreditCard},
            {key: "club", label: "Club", icon: HandCoins},
        ],
        [t],
    );

    const effectiveFilters = cleanFiltersForType(type, filters, filters.page || 1, pageSize);
    const labels = activeFilterLabels(type, filters, t);

    const applyFilters = () => {
        setFilters({...draft, page: 1});
    };

    const clearFilters = () => {
        setDraft(emptyDraft);
        setFilters({page: 1});
        setPageSize(DEFAULT_PAY_REPORT_PAGE_SIZE);
    };

    const changePage = (page) => {
        setFilters((current) => ({...current, page}));
    };

    const changePageSize = (nextPageSize) => {
        setPageSize(nextPageSize);
        setFilters((current) => ({...current, page: 1}));
    };

    const switchType = (nextType) => {
        setType(nextType);
        setFilters((current) => ({...current, page: 1}));
    };

    const changePayView = (nextView) => {
        setPayView(nextView);
        try {
            localStorage.setItem(PAY_VIEW_STORAGE_KEY, nextView);
        } catch (_error) {
            /* ignore quota / private mode */
        }
        setFilters((current) => ({...current, page: 1}));
    };

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <div>
                    <p className="admin-eyebrow">{t("money.finance.eyebrow")}</p>
                    <h1 className="admin-page-title">{t("money.finance.title")}</h1>
                    <p className="admin-page-copy">
                        {t("money.finance.copy")}
                    </p>
                </div>
                <div className="finance-header-actions">
                    {capabilities.hasAction("refunds.view") ? (
                        <Link className="admin-button admin-button-secondary" to="/finance/refunds">
                            <RotateCcw aria-hidden="true" size={16} /> {t("money.finance.refundsLink")}
                        </Link>
                    ) : null}
                    {capabilities.hasAction("split_bills.view") ? (
                        <Link className="admin-button admin-button-secondary" to="/finance/split-bills">
                            <Split aria-hidden="true" size={16} /> Split bills
                        </Link>
                    ) : null}
                    <div className="admin-pill">
                        <Landmark size={14} /> Airba / Bakai
                    </div>
                </div>
            </header>

            <div className="admin-tabs" role="tablist" aria-label={t("money.finance.reportsAria")}>
                {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.key}
                            className={`admin-tab ${type === tab.key ? "is-active" : ""}`}
                            type="button"
                            onClick={() => switchType(tab.key)}
                        >
                            <Icon size={15} /> {tab.label}
                        </button>
                    );
                })}
            </div>

            <section className="admin-card">
                <div className="admin-section-head">
                    <div>
                        <p className="admin-card-title"><Filter size={14} /> {t("money.finance.filtersTitle")}</p>
                        <p className="admin-card-note">
                            {t("money.finance.filtersNote")}
                        </p>
                    </div>
                    {labels.length > 0 && (
                        <div className="admin-filter-tags">
                            {labels.map((item) => <span key={item}>{item}</span>)}
                        </div>
                    )}
                </div>
                <PageToolbar>
                    <ToolbarInput
                        label={t("money.finance.filters.dateFrom")}
                        type="date"
                        value={draft.date_from}
                        onChange={(event) => setDraft((value) => ({...value, date_from: event.target.value}))}
                    />
                    <ToolbarInput
                        label={t("money.finance.filters.dateTo")}
                        type="date"
                        value={draft.date_to}
                        onChange={(event) => setDraft((value) => ({...value, date_to: event.target.value}))}
                    />

                    {type === "pay" && (
                        <>
                            <PayNameFilter
                                kind="place"
                                label={draft.place_label}
                                placeholder={t("money.finance.filters.nameOrId")}
                                value={draft.place}
                                onSelect={(item) => setDraft((value) => ({...value, place: String(item.id), place_label: item.name}))}
                                onClear={() => setDraft((value) => ({...value, place: "", place_label: ""}))}
                            />
                            <PayNameFilter
                                kind="branch"
                                label={draft.branch_label}
                                placeholder={t("money.finance.filters.nameOrId")}
                                value={draft.branch}
                                onSelect={(item) => setDraft((value) => ({...value, branch: String(item.id), branch_label: item.name}))}
                                onClear={() => setDraft((value) => ({...value, branch: "", branch_label: ""}))}
                            />
                        </>
                    )}

                    {type === "topups" && (
                        <ToolbarSelect
                            label={t("money.finance.filters.role")}
                            value={draft.role}
                            onChange={(event) => setDraft((value) => ({...value, role: event.target.value}))}
                        >
                            <option value="">{t("money.finance.all")}</option>
                            <option value="1">{t("money.finance.filters.users")}</option>
                            <option value="2">{t("money.finance.filters.clubMembers")}</option>
                        </ToolbarSelect>
                    )}

                    <ToolbarButton onClick={applyFilters}>
                        <Search size={15} /> {t("money.finance.apply")}
                    </ToolbarButton>
                    <ToolbarButton variant="secondary" onClick={clearFilters}>
                        {t("money.finance.reset")}
                    </ToolbarButton>
                </PageToolbar>
            </section>

            <FinanceDataset
                type={type}
                filters={effectiveFilters}
                pageSize={pageSize}
                payView={payView}
                onPayViewChange={changePayView}
                onPageChange={changePage}
                onPageSizeChange={changePageSize}
            />
        </div>
    );
};
